import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarClock, ExternalLink } from "lucide-react";

const DEFAULT_URL = "https://calendly.com/divyshetty/consultation";

/**
 * Calendly popup widget. Loads Calendly script lazily on first open.
 * Pass `url` prop to override, otherwise uses DEFAULT_URL.
 */
export const CalendlyEmbed = ({ open, onClose, url = DEFAULT_URL }) => {
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (window.Calendly) {
      setScriptReady(true);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.onload = () => setScriptReady(true);
    document.body.appendChild(s);

    if (!document.querySelector("link[data-calendly]")) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://assets.calendly.com/assets/external/widget.css";
      l.setAttribute("data-calendly", "1");
      document.head.appendChild(l);
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="calendly-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-4xl h-[80vh] bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-3xl overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 border-b border-white/5 bg-[#0A0A0A] z-10">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-sm text-white">Book a 30-min consultation</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calendly-open-new-tab"
                  className="text-xs text-[#B0B0B0] hover:text-[#D4AF37] inline-flex items-center gap-1"
                >
                  Open in new tab <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  data-testid="calendly-close"
                  onClick={onClose}
                  aria-label="Close"
                  className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[#B0B0B0] hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="pt-12 h-full">
              {scriptReady ? (
                <iframe
                  title="Calendly"
                  src={`${url}?hide_landing_page_details=1&hide_gdpr_banner=1&background_color=0a0a0a&text_color=ffffff&primary_color=d4af37`}
                  className="w-full h-full border-0"
                  data-testid="calendly-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-[#B0B0B0] text-sm">
                  Loading scheduler…
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
